import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getSectionConfig } from "../../pages/docs/config/sections";
import { isDocsLocalMode } from "../../pages/docs/constants/docsEnv";
import { loadSectionConfig } from "../../utils/api/docs_apis";
import { isRenderableSectionConfig } from "../../utils/docUtils/docsSectionUtils";
import { CommonBlockRenderer } from "../commonBlockRenderer";
import DocsContentSpinner from "./DocsContentSpinner";
import DocsUnavailable from "./DocsUnavailable";

function resolveSectionState(config) {
  if (!isRenderableSectionConfig(config)) {
    return { status: "error", config: null };
  }

  return { status: "ready", config };
}

function getInitialSectionState(sectionId) {
  if (!isDocsLocalMode()) {
    return { status: "loading", config: null };
  }

  return resolveSectionState(getSectionConfig(sectionId));
}

const DocsSection = ({ sectionId, docNav }) => {
  const [sectionState, setSectionState] = useState(() => getInitialSectionState(sectionId));

  useEffect(() => {
    if (isDocsLocalMode()) {
      setSectionState(resolveSectionState(getSectionConfig(sectionId)));
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setSectionState({ status: "loading", config: null });
      const config = await loadSectionConfig(docNav, sectionId);

      if (cancelled) {
        return;
      }

      setSectionState(resolveSectionState(config));
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [sectionId, docNav]);

  if (sectionState.status === "loading") {
    return <DocsContentSpinner />;
  }

  if (sectionState.status === "error") {
    return <DocsUnavailable />;
  }

  return (
    <motion.div
      key={sectionId}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <CommonBlockRenderer section={sectionState.config} />
    </motion.div>
  );
};

export default DocsSection;
